import React from 'react';
import { 
  NavBar, 
  Card, 
  List, 
  Button,
  Space,
  Divider
} from 'antd-mobile';
import { 
  LeftOutline, 
  HeartOutline,
  StarOutline,
  GlobalOutline,
  MailOutline
} from 'antd-mobile-icons';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '@/hooks/useI18n';

const AboutUs: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const handleBack = () => {
    navigate(-1);
  };

  const handleContact = () => {
    window.open('mailto:support@zevolve.ai');
  };

  const handleWebsite = () => {
    window.open('https://zevolve.ai', '_blank');
  };

  const handleRate = () => {
    // 跳转到应用商店评分页面
    console.log('Rate app');
  };

  const features = [
    { icon: '🤖', text: t('about.feature1') },
    { icon: '💬', text: t('about.feature2') },
    { icon: '🎯', text: t('about.feature3') },
    { icon: '🔒', text: t('about.feature4') }
  ];

  const contacts = [
    { icon: MailOutline, text: 'support@zevolve.ai', action: handleContact },
    { icon: GlobalOutline, text: 'www.zevolve.ai', action: handleWebsite }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex justify-center">
      <div className="w-full min-h-screen relative overflow-hidden" style={{ maxWidth: '480px' }}>
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl" />
      </div>

      {/* 顶部导航 */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <NavBar 
          onBack={handleBack}
          className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50"
        >
          <div className="flex items-center text-gray-800">
            <HeartOutline className="mr-2" />
            {t('settings.aboutUs')}
          </div>
        </NavBar>
      </motion.div>

      <div className="p-4 relative z-10">
        {/* 应用信息卡片 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <motion.div 
              className="p-8 text-center"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.6, delay: 0.3, type: "spring", stiffness: 200 }}
                className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
              >
                <span className="text-white text-3xl font-bold">M</span>
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                ZevolveAI
              </h2>
              <p className="text-gray-500 text-sm mb-4 leading-relaxed">
                {t('about.appDescription')}
              </p>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                className="inline-block px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-full border border-blue-100"
              >
                <span className="text-gray-700 text-sm font-medium">
                  {t('about.version')} 1.0.0
                </span>
              </motion.div>
            </motion.div>
          </Card>
        </motion.div>

        {/* 功能特色 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full mr-3" />
                {t('about.features')}
              </h3>
              <div className="space-y-3">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                    whileHover={{ x: 4, scale: 1.02 }}
                    className="p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl border border-gray-200 hover:border-blue-200 transition-all duration-200"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3">
                        <span className="text-lg">{feature.icon}</span>
                      </div>
                      <span className="text-gray-700 font-medium">{feature.text}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 开发团队 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full mr-3" />
                {t('about.team')}
              </h3>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100"
              >
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t('about.teamDescription')}
                </p>
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* 联系我们 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-red-500 rounded-full mr-3" />
                {t('about.contact')}
              </h3>
              <div className="space-y-3">
                {contacts.map((contact, index) => {
                  const IconComponent = contact.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                      whileHover={{ x: 4, scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={contact.action}
                      className="p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl border border-gray-200 hover:border-blue-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mr-3">
                          <IconComponent className="text-white" />
                        </div>
                        <span className="text-gray-700 font-medium">{contact.text}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 操作按钮 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="mt-6 space-y-3"
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              block 
              color="primary"
              onClick={handleRate}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 border-0 rounded-xl h-12 font-medium shadow-lg flex items-center justify-center"
            >
              <StarOutline className="mr-2" />
              {t('about.rateApp')}
            </Button>
          </motion.div>
          
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button 
              block 
              fill="outline"
              onClick={handleContact}
              className="border-2 border-blue-200 text-blue-600 rounded-xl h-12 font-medium hover:bg-blue-50 transition-colors duration-200"
            >
              <MailOutline className="mr-2" />
              {t('about.contactUs')}
            </Button>
          </motion.div>
        </motion.div>

        {/* 版权信息 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="mt-8 text-center"
        >
          <Card className="bg-white/60 backdrop-blur-xl border-0 shadow-lg rounded-2xl overflow-hidden">
            <div className="p-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <Divider className="text-gray-400 text-xs">{t('about.copyright')}</Divider>
                <p className="text-gray-400 text-xs mt-2">
                  © 2025 ZEVOLVE AI
                </p>
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* 底部间距 */}
        <div className="h-8" />
      </div>
      </div>
    </div>
  );
};

export default AboutUs;